/* BeforeAfterSlider — pointer/touch drag slider comparing original + variant image. */
import React, { useState, useRef, useCallback } from 'react';

export default function BeforeAfterSlider({ before, after, label = '' }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const update = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  }, []);

  const onDown = (e) => {
    draggingRef.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    update(cx);
  };
  const onMove = (e) => {
    if (!draggingRef.current) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    update(cx);
  };
  const onUp = () => { draggingRef.current = false; };

  const beforeSrc = before?.startsWith('data:') ? before : `data:image/jpeg;base64,${before}`;
  const afterSrc = after?.startsWith('data:') ? after : `data:image/png;base64,${after}`;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-64 overflow-hidden rounded-xl border border-emerald-200 select-none touch-none bg-slate-900"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      data-testid={`before-after-slider${label ? `-${label}` : ''}`}
    >
      {/* Before (bottom layer) */}
      <img src={beforeSrc} alt="före" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute top-2 left-2 bg-slate-900/70 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
        Före
      </div>
      {/* After (clipped to right of slider) */}
      <div
        className="absolute inset-y-0 right-0 overflow-hidden pointer-events-none"
        style={{ width: `${100 - pos}%` }}
      >
        <img
          src={afterSrc}
          alt="efter"
          className="absolute inset-y-0 right-0 h-full object-cover"
          style={{ width: `${(100 / (100 - pos)) * 100}%`, maxWidth: 'none' }}
          draggable={false}
        />
      </div>
      <div className="absolute top-2 right-2 bg-emerald-500/90 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
        Efter
      </div>
      {/* Slider line + handle */}
      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-0.5 bg-white/90 shadow-md" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center cursor-ew-resize pointer-events-auto">
          <div className="flex gap-0.5">
            <div className="w-1 h-3 bg-emerald-500 rounded-full" />
            <div className="w-1 h-3 bg-emerald-500 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
